import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FilePlus2,
  Receipt,
  TrendingUp,
  Wallet,
  Zap,
  RefreshCw,
  ChevronRight,
  Activity,
} from 'lucide-react';
import { BarChart, Bar, CartesianGrid, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Layout from '../components/Layout';
import { api, companyProfileAPI } from '../api/client';
import { getDefaultCurrency, formatCurrency } from '../utils/currency';

// ─── Date helpers ─────────────────────────────────────────────────────────────
const TODAY = new Date();
const formatDay = (date) => new Date(date).toISOString().split('T')[0];
const toSafeNumber = (value) => { const n = parseFloat(value); return Number.isFinite(n) ? n : 0; };
const startOfMonth   = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1);
const startOfLast    = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1);
const endOfLast      = new Date(TODAY.getFullYear(), TODAY.getMonth(), 0);
const startOfQuarter = new Date(TODAY.getFullYear(), Math.floor(TODAY.getMonth() / 3) * 3, 1);
const startOfYear    = new Date(TODAY.getFullYear(), 0, 1);
const inRange = (value, s, e) => { if (!value) return false; const d = formatDay(value); return d >= s && d <= e; };

const DATE_PRESETS = [
  { label: 'This Month',    start: formatDay(startOfMonth),   end: formatDay(TODAY) },
  { label: 'Last Month',    start: formatDay(startOfLast),    end: formatDay(endOfLast) },
  { label: 'This Quarter',  start: formatDay(startOfQuarter), end: formatDay(TODAY) },
  { label: 'This Year',     start: formatDay(startOfYear),    end: formatDay(TODAY) },
];

// ─── Status badge ──────────────────────────────────────────────────────────────
const statusTone = (status) => {
  switch (status) {
    case 'paid':           return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'pending':        return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'partially_paid': return 'border-orange-200 bg-orange-50 text-orange-700';
    case 'draft':          return 'border-slate-200 bg-slate-100 text-slate-700';
    case 'completed':      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'in-progress':    return 'border-blue-200 bg-blue-50 text-blue-700';
    case 'planning':       return 'border-indigo-200 bg-indigo-50 text-indigo-700';
    case 'on-hold':        return 'border-yellow-200 bg-yellow-50 text-yellow-700';
    default:               return 'border-rose-200 bg-rose-50 text-rose-700';
  }
};

// ─── Activity type metadata ────────────────────────────────────────────────────
const activityMeta = (type) => {
  switch (type) {
    case 'invoice': return { bg: 'bg-blue-500',    Icon: FilePlus2 };
    case 'expense': return { bg: 'bg-rose-500',    Icon: Receipt };
    case 'project': return { bg: 'bg-emerald-500', Icon: Briefcase };
    default:        return { bg: 'bg-slate-400',   Icon: Activity };
  }
};

// ─── Custom recharts tooltip ───────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl border border-black/5 bg-white/95 px-3 py-2 text-sm shadow-lg backdrop-blur">
      <p className="font-bold text-slate-900">{formatCurrency(payload[0]?.value || 0, currency)}</p>
    </div>
  );
};

// ─── Sub-components defined OUTSIDE Dashboard to prevent re-creation ───────────
const StatCard = ({ title, value, subtitle, icon: Icon, accent, eyebrow }) => (
  <div className={`relative overflow-hidden rounded-[28px] p-6 shadow-lg ${accent}`}>
    <div
      className="pointer-events-none absolute inset-0"
      style={{ background: 'radial-gradient(circle at top right, rgba(255,255,255,0.16) 0%, transparent 60%)' }}
    />
    <div className="relative flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1 overflow-hidden">
        {eyebrow ? <p className="text-[0.62rem] font-bold uppercase tracking-[0.24em] text-white/70">{eyebrow}</p> : null}
        <p className="mt-1 text-sm font-semibold text-white/85">{title}</p>
        <p className="mt-3 truncate text-3xl font-extrabold leading-tight text-white">{value}</p>
        {subtitle ? <p className="mt-2 text-sm text-white/72">{subtitle}</p> : null}
      </div>
      <div className="flex-shrink-0 rounded-[22px] bg-white/15 p-3 ring-1 ring-white/10 backdrop-blur">
        <Icon size={22} className="text-white/95" />
      </div>
    </div>
  </div>
);

const MiniMetric = ({ title, value, note, tone, icon: Icon }) => (
  <div className={`rounded-[24px] border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${tone}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {Icon ? <Icon size={15} className="flex-shrink-0 opacity-70 text-slate-700" /> : null}
    </div>
    <p className="mt-2 text-2xl font-extrabold leading-none text-slate-950">{value}</p>
    {note ? <p className="mt-2 text-xs text-slate-500">{note}</p> : null}
  </div>
);

const SectionCard = ({ eyebrow, title, description, children, className = '' }) => (
  <div className={`rounded-[28px] border border-black/5 bg-white/86 p-6 shadow-[0_18px_44px_rgba(23,34,28,0.08)] backdrop-blur-xl ${className}`}>
    <p className="app-page-eyebrow mb-1">{eyebrow}</p>
    <h3 className="text-lg font-bold text-slate-950">{title}</h3>
    {description ? <p className="mt-1 mb-5 text-sm text-slate-500">{description}</p> : null}
    {children}
  </div>
);

// ─── Main Dashboard ────────────────────────────────────────────────────────────
function Dashboard() {
  const [currency, setCurrency] = useState(getDefaultCurrency());
  const [companyDetails, setCompanyDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: DATE_PRESETS[0].start,
    endDate:   DATE_PRESETS[0].end,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, companyRes, analyticsRes] = await Promise.all([
        api.get('/settings'),
        companyProfileAPI.get(),
        api.get('/reports/analytics', {
          params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
        }),
      ]);
      const analytics = analyticsRes.data || {};
      setCompanyDetails({ ...settingsRes.data, ...companyRes.data });
      setInvoices(analytics.invoices   || []);
      setExpenses(analytics.expenses   || []);
      setProjects(analytics.projects   || []);
      setCustomers(analytics.customers || []);
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const handle = (e) => setCurrency(e.detail.currency);
    window.addEventListener('currencyChanged', handle);
    return () => window.removeEventListener('currencyChanged', handle);
  }, []);

  const applyPreset = (index) => {
    setActivePreset(index);
    setDateRange({ startDate: DATE_PRESETS[index].start, endDate: DATE_PRESETS[index].end });
  };

  // ── Derived metrics (all memoised) ──────────────────────────────────────────
  const filteredInvoices = useMemo(
    () => invoices.filter((inv) => inRange(inv.invoice_date || inv.created_at, dateRange.startDate, dateRange.endDate)),
    [invoices, dateRange]
  );
  const filteredExpenses = useMemo(
    () => expenses.filter((exp) => inRange(exp.date || exp.created_at, dateRange.startDate, dateRange.endDate)),
    [expenses, dateRange]
  );

  const paidInvoices         = useMemo(() => filteredInvoices.filter((i) => i.status === 'paid'),           [filteredInvoices]);
  const unpaidInvoices       = useMemo(() => filteredInvoices.filter((i) => i.status !== 'paid'),           [filteredInvoices]);
  const pendingInvoices      = useMemo(() => filteredInvoices.filter((i) => i.status === 'pending'),        [filteredInvoices]);
  const partiallyPaidInvoices = useMemo(() => filteredInvoices.filter((i) => i.status === 'partially_paid'), [filteredInvoices]);
  const draftInvoices        = useMemo(() => filteredInvoices.filter((i) => i.status === 'draft'),          [filteredInvoices]);
  const overdueInvoices      = useMemo(
    () => filteredInvoices.filter((i) => i.due_date && i.status !== 'paid' && formatDay(i.due_date) < formatDay(TODAY)),
    [filteredInvoices]
  );

  const totalIncome     = useMemo(() => paidInvoices.reduce((s, i) => s + toSafeNumber(i.total_amount || i.amount), 0), [paidInvoices]);
  const totalExpenses   = useMemo(() => filteredExpenses.reduce((s, e) => s + toSafeNumber(e.amount), 0),             [filteredExpenses]);
  const totalTax        = useMemo(() => filteredExpenses.reduce((s, e) => s + toSafeNumber(e.tax_amount), 0),         [filteredExpenses]);
  const netProfit       = totalIncome - totalExpenses;
  const totalReceivables = useMemo(() => unpaidInvoices.reduce((s, i) => s + toSafeNumber(i.total_amount || i.amount), 0), [unpaidInvoices]);
  const overdueAmount   = useMemo(() => overdueInvoices.reduce((s, i) => s + toSafeNumber(i.total_amount || i.amount), 0), [overdueInvoices]);

  const activeProjects    = useMemo(() => projects.filter((p) => !['completed', 'cancelled'].includes(String(p.status).toLowerCase())), [projects]);
  const completedProjects = useMemo(() => projects.filter((p) => String(p.status).toLowerCase() === 'completed'), [projects]);
  const nearingDeadlineProjects = useMemo(
    () => activeProjects.filter((p) => { if (!p.endDate) return false; const diff = Math.ceil((new Date(p.endDate) - TODAY) / 86400000); return diff >= 0 && diff <= 7; }),
    [activeProjects]
  );
  const uninvoicedProjects = useMemo(
    () => activeProjects.filter((p) => !invoices.some((inv) => String(inv.project_id || '') === String(p.id))),
    [activeProjects, invoices]
  );
  const dueSoonInvoices = useMemo(
    () => invoices.filter((inv) => { if (!inv.due_date || inv.status === 'paid') return false; const diff = Math.ceil((new Date(inv.due_date) - TODAY) / 86400000); return diff >= 0 && diff <= 7; }),
    [invoices]
  );

  const monthlyData = useMemo(() => {
    const map = paidInvoices.reduce((acc, inv) => {
      const k = formatDay(inv.invoice_date || inv.created_at).slice(0, 7);
      acc[k] = (acc[k] || 0) + toSafeNumber(inv.total_amount || inv.amount);
      return acc;
    }, {});
    return Object.entries(map).map(([month, total]) => ({ month, total })).sort((a, b) => a.month.localeCompare(b.month)).slice(-6);
  }, [paidInvoices]);

  const expenseData = useMemo(() => {
    const map = filteredExpenses.reduce((acc, exp) => {
      const c = exp.category || 'Other';
      acc[c] = (acc[c] || 0) + toSafeNumber(exp.amount);
      return acc;
    }, {});
    return Object.entries(map).map(([category, total]) => ({ category, total })).sort((a, b) => b.total - a.total).slice(0, 6);
  }, [filteredExpenses]);

  const customersById = useMemo(
    () => customers.reduce((acc, c) => { acc[String(c.id)] = c; return acc; }, {}),
    [customers]
  );

  const topCustomers = useMemo(
    () => Object.values(
      filteredInvoices.reduce((acc, inv) => {
        const key = String(inv.customer_id || 'unknown');
        if (!acc[key]) acc[key] = { customerName: customersById[key]?.name || `Customer ${inv.customer_id || ''}`.trim(), billed: 0, paid: 0, invoices: 0 };
        acc[key].billed += toSafeNumber(inv.total_amount || inv.amount);
        if (inv.status === 'paid') acc[key].paid += toSafeNumber(inv.total_amount || inv.amount);
        acc[key].invoices += 1;
        return acc;
      }, {})
    ).sort((a, b) => b.billed - a.billed).slice(0, 5),
    [filteredInvoices, customersById]
  );

  const recentInvoices = useMemo(
    () => [...invoices].sort((a, b) => new Date(b.created_at || b.invoice_date) - new Date(a.created_at || a.invoice_date)).slice(0, 5),
    [invoices]
  );
  const recentExpenses = useMemo(
    () => [...expenses].sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date)).slice(0, 5),
    [expenses]
  );
  const recentActivity = useMemo(() => [
    ...recentInvoices.map((inv) => ({ id: `invoice-${inv.id}`, title: `Invoice ${inv.invoice_number || `#${inv.id}`} ${inv.status === 'paid' ? 'paid' : 'created'}`, subtitle: customersById[String(inv.customer_id)]?.name || 'Invoice activity', date: inv.created_at || inv.invoice_date, type: 'invoice', href: `/invoices/${inv.id}/preview` })),
    ...recentExpenses.map((exp) => ({ id: `expense-${exp.id}`, title: `Expense: ${exp.description || 'Expense'}`, subtitle: exp.category || 'Expense', date: exp.created_at || exp.date, type: 'expense', href: '/expenses' })),
    ...projects.slice().sort((a, b) => new Date(b.created_at || b.startDate) - new Date(a.created_at || a.startDate)).slice(0, 5).map((p) => ({ id: `project-${p.id}`, title: `Project: ${p.projectName}`, subtitle: p.status, date: p.created_at || p.startDate, type: 'project', href: '/projects' })),
  ].filter((item) => item.date).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8),
  [recentInvoices, recentExpenses, projects, customersById]);

  const insights = useMemo(() => [
    overdueInvoices.length > 0    ? { color: 'rose',   Icon: AlertCircle,  text: `${overdueInvoices.length} overdue invoice${overdueInvoices.length > 1 ? 's are' : ' is'} holding up ${formatCurrency(overdueAmount, currency)}.` } : null,
    uninvoicedProjects.length > 0 ? { color: 'amber',  Icon: Briefcase,    text: `${uninvoicedProjects.length} active project${uninvoicedProjects.length > 1 ? 's have' : ' has'} no linked invoice yet.` } : null,
    nearingDeadlineProjects.length > 0 ? { color: 'orange', Icon: CalendarClock, text: `${nearingDeadlineProjects.length} project${nearingDeadlineProjects.length > 1 ? 's are' : ' is'} nearing deadline within 7 days.` } : null,
    pendingInvoices.length > 0    ? { color: 'blue',   Icon: FilePlus2,    text: `${pendingInvoices.length} pending invoice${pendingInvoices.length > 1 ? 's total' : ' totals'} ${formatCurrency(pendingInvoices.reduce((s, i) => s + toSafeNumber(i.total_amount || i.amount), 0), currency)}.` } : null,
  ].filter(Boolean), [overdueInvoices, uninvoicedProjects, nearingDeadlineProjects, pendingInvoices, overdueAmount, currency]);

  const invoiceFunnel = useMemo(() => [
    { label: 'Draft',   count: draftInvoices.length,         bar: '#94a3b8' },
    { label: 'Pending', count: pendingInvoices.length,       bar: '#f59e0b' },
    { label: 'Partial', count: partiallyPaidInvoices.length, bar: '#f97316' },
    { label: 'Paid',    count: paidInvoices.length,          bar: '#10b981' },
    { label: 'Overdue', count: overdueInvoices.length,       bar: '#e11d48' },
  ], [draftInvoices, pendingInvoices, partiallyPaidInvoices, paidInvoices, overdueInvoices]);

  const totalFunnel   = Math.max(invoiceFunnel.reduce((s, i) => s + i.count, 0), 1);
  const healthPercent = totalIncome > 0 ? Math.round((netProfit / totalIncome) * 100) : null;

  return (
    <Layout pageTitle="Financial Command Center" pageSubtitle="Your command center for cash flow, projects, and follow-ups.">
      <div className="app-page-shell space-y-6">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="overflow-hidden rounded-[30px] border border-black/5 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.18),_transparent_34%),linear-gradient(135deg,#1d1f1b_0%,#156b4a_48%,#de7f32_100%)] px-6 py-5 text-white shadow-[0_24px_58px_rgba(23,34,28,0.2)] sm:px-7 sm:py-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-[0.64rem] font-bold uppercase tracking-[0.26em] text-white/65">Financial Command Center</p>
              <h2 className="mt-1.5 max-w-3xl text-[2rem] font-extrabold tracking-tight sm:text-[2.2rem]">
                {companyDetails?.companyName || 'SolarAiTh Solutions'}
              </h2>
              <p className="mt-1.5 max-w-2xl text-sm leading-5 text-white/78 sm:text-[0.95rem]">
                A live view of collections, profitability, project momentum, and next actions across your business.
              </p>

              <div className="mt-2.5 flex flex-wrap gap-2">
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/88 backdrop-blur">
                  {dateRange.startDate} &rarr; {dateRange.endDate}
                </span>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/88 backdrop-blur">
                  {customers.length} customers
                </span>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/88 backdrop-blur">
                  {projects.length} projects tracked
                </span>
                {healthPercent !== null && (
                  <span className={`rounded-full px-3 py-1.5 text-xs font-bold ${healthPercent >= 0 ? 'bg-emerald-500/28 text-emerald-100' : 'bg-rose-500/28 text-rose-100'}`}>
                    {healthPercent >= 0 ? '▲' : '▼'} {Math.abs(healthPercent)}% margin
                  </span>
                )}
              </div>
            </div>

            <div className="grid gap-2.5 sm:grid-cols-2 xl:w-[560px] xl:grid-cols-2 xl:flex-shrink-0">
              <div className="rounded-[24px] border border-white/14 bg-white/10 p-3.5 backdrop-blur-xl">
                <p className="text-[0.62rem] font-bold uppercase tracking-[0.24em] text-white/65">Performance</p>
                <p className="mt-1 text-sm font-semibold text-white/88">Net position</p>
                <p className="mt-1.5 text-[1.7rem] font-extrabold text-white">{formatCurrency(netProfit, currency)}</p>
                <p className="mt-1 text-xs leading-5 text-white/72">
                  {netProfit >= 0 ? 'You are operating above expense load in this window.' : 'Expenses are currently ahead of collected revenue.'}
                </p>
              </div>

              <div className="rounded-[24px] border border-white/14 bg-white/10 p-3.5 backdrop-blur-xl">
                <p className="text-[0.62rem] font-bold uppercase tracking-[0.24em] text-white/65">Collections</p>
                <p className="mt-1 text-sm font-semibold text-white/88">Outstanding receivables</p>
                <p className="mt-1.5 text-[1.7rem] font-extrabold text-white">{formatCurrency(totalReceivables, currency)}</p>
                <p className="mt-1 text-xs leading-5 text-white/72">
                  {unpaidInvoices.length} unpaid invoice{unpaidInvoices.length === 1 ? '' : 's'} still need follow-up.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Workspace Rail ──────────────────────────────────────────── */}
        <div className="grid items-stretch gap-5 xl:grid-cols-[minmax(0,1fr),330px]">
          <div className="flex h-full flex-col rounded-[28px] border border-black/5 bg-white/86 p-4 shadow-[0_18px_44px_rgba(23,34,28,0.08)] backdrop-blur-xl">
            <div className="flex flex-nowrap items-center gap-3 overflow-x-auto">
              <div className="flex flex-shrink-0 overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm">
                {DATE_PRESETS.map((preset, i) => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(i)}
                    className={`px-4 py-2.5 text-sm transition ${i === activePreset ? 'bg-emerald-700 font-bold text-white' : 'bg-white font-semibold text-slate-600 hover:bg-[#f7f4ef]'} ${i > 0 ? 'border-l border-black/5' : ''}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-shrink-0 items-center gap-2">
                <div className="rounded-2xl border border-black/5 bg-white/90 px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Start</p>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => { setActivePreset(-1); setDateRange((p) => ({ ...p, startDate: e.target.value })); }}
                    className="[color-scheme:light] min-w-[150px] appearance-none border-0 bg-transparent px-0 py-0.5 text-sm font-medium text-slate-700 shadow-none focus:border-0 focus:outline-none focus:ring-0"
                  />
                </div>
                <span className="text-slate-400">→</span>
                <div className="rounded-2xl border border-black/5 bg-white/90 px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">End</p>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => { setActivePreset(-1); setDateRange((p) => ({ ...p, endDate: e.target.value })); }}
                    className="[color-scheme:light] min-w-[150px] appearance-none border-0 bg-transparent px-0 py-0.5 text-sm font-medium text-slate-700 shadow-none focus:border-0 focus:outline-none focus:ring-0"
                  />
                </div>

                <button
                  onClick={loadData}
                  disabled={loading}
                  className="btn-primary flex items-center gap-2 px-4 py-2.5 text-sm"
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                  {loading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Window</p>
                <p className="mt-1 text-sm font-semibold text-slate-950">
                  {activePreset >= 0 ? DATE_PRESETS[activePreset].label : 'Custom Range'}
                </p>
              </div>
              <div className="rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Invoices In View</p>
                <p className="mt-1 text-sm font-semibold text-slate-950">{filteredInvoices.length} records</p>
              </div>
              <div className="rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Expenses In View</p>
                <p className="mt-1 text-sm font-semibold text-slate-950">{filteredExpenses.length} records</p>
              </div>
            </div>

            <div className="mt-4 rounded-[24px] border border-black/5 bg-[linear-gradient(180deg,rgba(255,255,255,0.92)_0%,rgba(247,244,239,0.92)_100%)] px-4 py-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Range Summary</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">
                    {formatCurrency(totalIncome, currency)} in revenue vs {formatCurrency(totalExpenses, currency)} in spend
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${netProfit >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {netProfit >= 0 ? 'Positive' : 'Negative'} Net
                </span>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Paid</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{paidInvoices.length} invoices</p>
                </div>
                <div className="rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Pending</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{pendingInvoices.length} invoices</p>
                </div>
                <div className="rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Overdue</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{overdueInvoices.length} invoices</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex h-full flex-col rounded-[28px] border border-black/5 bg-[linear-gradient(180deg,rgba(255,255,255,0.94)_0%,rgba(248,245,239,0.92)_100%)] p-5 shadow-[0_18px_44px_rgba(23,34,28,0.08)]">
            <p className="app-page-eyebrow mb-1">Today&apos;s Focus</p>
            <h3 className="text-lg font-bold text-slate-950">Operations Snapshot</h3>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <span className="text-sm text-slate-500">Revenue collected</span>
                <span className="text-sm font-bold text-slate-950">{formatCurrency(totalIncome, currency)}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <span className="text-sm text-slate-500">Open project load</span>
                <span className="text-sm font-bold text-slate-950">{activeProjects.length} active</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <span className="text-sm text-slate-500">Urgent invoices</span>
                <span className="text-sm font-bold text-slate-950">{overdueInvoices.length + dueSoonInvoices.length}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3">
                <span className="text-sm text-slate-500">Top expense bucket</span>
                <span className="text-sm font-bold text-slate-950">{expenseData[0]?.category || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Primary KPI Cards ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Revenue" eyebrow="Collected" value={formatCurrency(totalIncome, currency)} subtitle={`${paidInvoices.length} paid invoice${paidInvoices.length === 1 ? '' : 's'}`} icon={TrendingUp} accent="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white" />
          <StatCard title="Expenses" eyebrow="Spent" value={formatCurrency(totalExpenses, currency)} subtitle={`${filteredExpenses.length} expense${filteredExpenses.length === 1 ? '' : 's'}`} icon={Wallet} accent="bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white" />
          <StatCard title="Net Profit" eyebrow="Margin" value={formatCurrency(netProfit, currency)} subtitle={netProfit >= 0 ? 'Healthy margin profile' : 'Expenses exceed revenue'} icon={Zap} accent={netProfit >= 0 ? 'bg-gradient-to-br from-slate-900 via-emerald-900 to-emerald-700 text-white' : 'bg-gradient-to-br from-slate-900 via-rose-800 to-orange-600 text-white'} />
          <StatCard title="Receivables" eyebrow="Outstanding" value={formatCurrency(totalReceivables, currency)} subtitle={`${unpaidInvoices.length} unpaid invoice${unpaidInvoices.length === 1 ? '' : 's'}`} icon={Clock3} accent="bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-700 text-white" />
        </div>

        {/* ── Secondary Metrics ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MiniMetric title="Overdue" value={formatCurrency(overdueAmount, currency)} note={`${overdueInvoices.length} overdue invoice${overdueInvoices.length === 1 ? '' : 's'}`} tone="border-rose-200 bg-rose-50/90" icon={AlertCircle} />
          <MiniMetric title="Pending Collection" value={formatCurrency(pendingInvoices.reduce((s, i) => s + toSafeNumber(i.total_amount || i.amount), 0), currency)} note={`${pendingInvoices.length} pending`} tone="border-amber-200 bg-amber-50/90" icon={Clock3} />
          <MiniMetric title="Due This Week" value={`${dueSoonInvoices.length}`} note="Invoices needing follow-up" tone="border-blue-200 bg-blue-50/90" icon={CalendarClock} />
          <MiniMetric title="Expense Tax" value={formatCurrency(totalTax, currency)} note="Tax component in range" tone="border-emerald-200 bg-emerald-50/90" icon={Receipt} />
        </div>

        {/* ── Charts Row ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <SectionCard eyebrow="Revenue Trend" title="Monthly Paid Revenue" description="Last 6 months of collected payments">
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={monthlyData}>
                  <defs>
                    <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#7c3aed" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip currency={currency} />} />
                  <Line type="monotone" dataKey="total" stroke="url(#lineGrad)" strokeWidth={3} dot={{ r: 5, fill: '#06b6d4', stroke: '#ffffff', strokeWidth: 2 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[240px] items-center justify-center rounded-2xl bg-[#f7f4ef] text-sm text-slate-500">No revenue data in this window</div>
            )}
          </SectionCard>

          <SectionCard eyebrow="Expense Breakdown" title="Spending by Category" description="Top 6 categories in the selected window">
            {expenseData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={expenseData}>
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c3aed" />
                      <stop offset="100%" stopColor="#4f46e5" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="category" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip currency={currency} />} />
                  <Bar dataKey="total" fill="url(#barGrad)" radius={[8, 8, 0, 0]} maxBarSize={52} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[240px] items-center justify-center rounded-2xl bg-[#f7f4ef] text-sm text-slate-500">No expense data in this window</div>
            )}
          </SectionCard>
        </div>

        {/* ── Invoice Funnel + Quick Insights + Project Health ─────────── */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">

          {/* Invoice Funnel */}
          <SectionCard eyebrow="Pipeline" title="Invoice Funnel" description="Where invoices are stacking up">
            <div className="space-y-5">
              {invoiceFunnel.map((item) => (
                <div key={item.label}>
                  <div className="mb-1.5 flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-600">{item.label}</span>
                    <span className="font-bold text-slate-950">{item.count}</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.max((item.count / totalFunnel) * 100, item.count > 0 ? 8 : 0)}%`, background: item.bar }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Quick Insights */}
          <SectionCard eyebrow="Alerts" title="Quick Insights" description="What deserves attention right now">
            <div className="space-y-3">
              {insights.length > 0 ? (
                insights.map((insight) => {
                  const InsightIcon = insight.Icon;
                  const colMap = {
                    rose:  'border-rose-200 bg-rose-50 text-rose-700',
                    amber: 'border-amber-200 bg-amber-50 text-amber-700',
                    orange: 'border-orange-200 bg-orange-50 text-orange-700',
                    blue:  'border-blue-200 bg-blue-50 text-blue-700',
                  };
                  return (
                    <div key={insight.text} className={`flex items-start gap-3 rounded-xl border p-4 ${colMap[insight.color] || 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                      <InsightIcon size={15} className="mt-0.5 flex-shrink-0 opacity-70" />
                      <p className="text-sm leading-relaxed">{insight.text}</p>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700">
                  <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                  <p className="text-sm">Everything looks steady. No urgent issues in this window.</p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Project Health */}
          <SectionCard eyebrow="Projects" title="Project Health" description="Progress and invoicing coverage">
            <div className="grid grid-cols-2 gap-3">
              <MiniMetric title="Active"        value={`${activeProjects.length}`}          note="In progress / planning"  tone="border-blue-200 bg-blue-50/90"     icon={Briefcase}     />
              <MiniMetric title="Completed"     value={`${completedProjects.length}`}        note="Closed successfully"     tone="border-emerald-200 bg-emerald-50/90" icon={CheckCircle2} />
              <MiniMetric title="Near Deadline" value={`${nearingDeadlineProjects.length}`}  note="Ending within 7 days"    tone="border-amber-200 bg-amber-50/90"  icon={CalendarClock} />
              <MiniMetric title="Uninvoiced"    value={`${uninvoicedProjects.length}`}       note="No linked invoice"       tone="border-rose-200 bg-rose-50/90"     icon={AlertCircle}   />
            </div>
            {uninvoicedProjects.slice(0, 2).map((project) => (
                <div key={project.id} className="mt-3 flex items-center justify-between rounded-2xl border border-black/5 bg-[#f7f4ef] px-4 py-3 text-slate-700">
                <div>
                  <p className="text-sm font-semibold text-slate-950">{project.projectName}</p>
                  <p className="text-xs text-slate-500">{project.clientName}</p>
                </div>
                <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusTone(project.status)}`}>{project.status}</span>
              </div>
            ))}
          </SectionCard>
        </div>

        {/* ── Upcoming Deadlines + Activity Feed ──────────────────────── */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">

          {/* Upcoming Deadlines */}
          <SectionCard eyebrow="Urgency" title="Upcoming Deadlines" description="Invoices and projects that need action soon">
            <div className="space-y-3">
              {dueSoonInvoices.slice(0, 4).map((invoice) => (
                <Link key={`inv-due-${invoice.id}`} to={`/invoices/${invoice.id}/preview`} className="group flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3.5 transition hover:-translate-y-0.5 hover:bg-white">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
                      <FilePlus2 size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-950">{invoice.invoice_number || `Invoice #${invoice.id}`}</p>
                      <p className="text-xs text-slate-500">Due {new Date(invoice.due_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-950">{formatCurrency(toSafeNumber(invoice.total_amount || invoice.amount), currency)}</span>
                    <ChevronRight size={13} className="text-slate-400 transition group-hover:text-slate-700" />
                  </div>
                </Link>
              ))}
              {nearingDeadlineProjects.slice(0, 3).map((project) => (
                <Link key={`proj-deadline-${project.id}`} to="/projects" className="group flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3.5 transition hover:-translate-y-0.5 hover:bg-white">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                      <Briefcase size={14} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-950">{project.projectName}</p>
                      <p className="text-xs text-slate-500">Deadline {new Date(project.endDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <ChevronRight size={13} className="text-slate-400 transition group-hover:text-slate-700" />
                </Link>
              ))}
              {dueSoonInvoices.length === 0 && nearingDeadlineProjects.length === 0 && (
                <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-700">
                  <CheckCircle2 size={15} className="flex-shrink-0 text-emerald-400" />
                  <p className="text-sm">Nothing urgent in the next 7 days.</p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Activity Feed */}
          <SectionCard eyebrow="Feed" title="Recent Activity" description="Invoices, expenses, and projects in one unified feed">
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => {
                  const meta = activityMeta(activity.type);
                  const AIcon = meta.Icon;
                  return (
                    <Link key={activity.id} to={activity.href} className="group flex items-start gap-3 rounded-2xl border border-black/5 bg-white/80 px-4 py-3.5 transition hover:-translate-y-0.5 hover:bg-white">
                      <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
                        <AIcon size={13} className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-950">{activity.title}</p>
                        <p className="text-xs text-slate-500">{activity.subtitle}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs text-slate-500">{new Date(activity.date).toLocaleDateString()}</p>
                        <ChevronRight size={11} className="ml-auto mt-1 text-slate-300 transition group-hover:text-slate-700" />
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="rounded-xl border border-black/5 bg-[#f7f4ef] p-4 text-sm text-slate-500">No recent activity yet.</div>
              )}
            </div>
          </SectionCard>
        </div>

        {/* ── Top Customers + Recent Invoices + Recent Expenses ────────── */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">

          {/* Top Customers */}
          <SectionCard eyebrow="Clients" title="Top Customers" description="Who is driving the most billed value">
            <div className="space-y-3">
              {topCustomers.length > 0 ? (
                topCustomers.map((customer, idx) => {
                  const payRate = customer.billed > 0 ? Math.round((customer.paid / customer.billed) * 100) : 0;
                  return (
                    <div key={customer.customerName} className="rounded-2xl border border-black/5 bg-white/80 p-4 text-slate-700">
                      <div className="mb-2 flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#f7f4ef] text-xs font-bold text-slate-700">
                            {idx + 1}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-950">{customer.customerName}</p>
                            <p className="text-xs text-slate-500">{customer.invoices} invoice{customer.invoices === 1 ? '' : 's'}</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-sm font-bold text-slate-950">{formatCurrency(customer.billed, currency)}</p>
                          <p className="text-xs text-emerald-400">Paid {formatCurrency(customer.paid, currency)}</p>
                        </div>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                        <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${payRate}%` }} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-xl border border-black/5 bg-[#f7f4ef] p-4 text-sm text-slate-500">No customer billing data in range.</div>
              )}
            </div>
          </SectionCard>

          {/* Recent Invoices */}
          <SectionCard eyebrow="Invoices" title="Recent Invoices">
            <div className="mb-5 flex items-center justify-between">
              <Link to="/invoices" className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">
                View all <ChevronRight size={13} />
              </Link>
            </div>
            <div className="space-y-3">
              {recentInvoices.length > 0 ? (
                recentInvoices.map((invoice) => (
                  <Link key={invoice.id} to={`/invoices/${invoice.id}/preview`} className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3.5 transition hover:-translate-y-0.5 hover:bg-white">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-950">{invoice.invoice_number || `Invoice #${invoice.id}`}</p>
                      <p className="text-xs text-slate-500">{customersById[String(invoice.customer_id)]?.name || 'Customer'}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-sm font-bold text-slate-950">{formatCurrency(toSafeNumber(invoice.total_amount || invoice.amount), currency)}</p>
                      <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${statusTone(invoice.status)}`}>{invoice.status || 'draft'}</span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="rounded-xl border border-black/5 bg-[#f7f4ef] p-4 text-sm text-slate-500">No invoices available yet.</div>
              )}
            </div>
          </SectionCard>

          {/* Recent Expenses */}
          <SectionCard eyebrow="Expenses" title="Recent Expenses">
            <div className="mb-5 flex items-center justify-between">
              <Link to="/expenses" className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">
                View all <ChevronRight size={13} />
              </Link>
            </div>
            <div className="space-y-3">
              {recentExpenses.length > 0 ? (
                recentExpenses.map((expense) => (
                  <Link key={expense.id} to="/expenses" className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/80 px-4 py-3.5 transition hover:-translate-y-0.5 hover:bg-white">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-950">{expense.description || 'Expense'}</p>
                      <p className="text-xs text-slate-500">{expense.category || 'General'}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-sm font-bold text-slate-950">{formatCurrency(toSafeNumber(expense.amount), currency)}</p>
                      <p className="text-xs text-slate-500">{new Date(expense.date || expense.created_at).toLocaleDateString()}</p>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="rounded-xl border border-black/5 bg-[#f7f4ef] p-4 text-sm text-slate-500">No expenses available yet.</div>
              )}
            </div>
          </SectionCard>
        </div>

      </div>
    </Layout>
  );
}

export default Dashboard;
